import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { supabase, supabaseWithoutCSRF } from "../supabaseClient";

/**
 * Hook to check if the current user has a password set
 * @returns {Object} { hasPassword, checkingPasswordStatus, checkPasswordStatus }
 *   - hasPassword: null (unknown), true (has password), false (no password)
 *   - checkingPasswordStatus: boolean indicating if check is in progress
 *   - checkPasswordStatus: function to manually trigger a check
 */
export const usePasswordStatus = () => {
  const { user } = useAuth();
  const [hasPassword, setHasPassword] = useState(null); // null = unknown, true/false = known
  const [checkingPasswordStatus, setCheckingPasswordStatus] = useState(false);

  const checkPasswordStatus = useCallback(async () => {
    if (!user || checkingPasswordStatus) return;

    setCheckingPasswordStatus(true);

    try {
      // Verify we have a valid session with an access token
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        // No valid session, default to null (unknown)
        setHasPassword(null);
        return;
      }

      // Check if user has a password by calling backend edge function
      // Explicitly pass Authorization header to ensure it's included
      const { data, error } = await supabaseWithoutCSRF.functions.invoke(
        "check-password-status",
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (!error && data?.hasPassword !== undefined) {
        setHasPassword(data.hasPassword);
      } else {
        // If check fails, default to null (unknown)
        // User can still set/change password
        setHasPassword(null);
      }
    } catch (error) {
      // Silently fail - default to null (unknown)
      setHasPassword(null);
    } finally {
      setCheckingPasswordStatus(false);
    }
  }, [user, checkingPasswordStatus]);

  // Check password status on mount and when user changes
  useEffect(() => {
    if (user) {
      checkPasswordStatus();
    }
  }, [user, checkPasswordStatus]);

  return {
    hasPassword,
    checkingPasswordStatus,
    checkPasswordStatus,
  };
};

